/**
 * Pinterest Ads — Report builders (Story 19.5, Task 3).
 *
 * Pure-data column lists + URL/body composition. No HTTP, no I/O — these
 * helpers feed `analytics.ts` (sync) and `reports.ts` (async) by
 * deterministically picking the per-report-type Pinterest column subset and
 * stamping it into the call's options.
 *
 * Module-load assertions enforce two structural invariants:
 *   1. Every column list is ≤ PINTEREST_ADS_MAX_COLUMNS_PER_REQUEST.
 *   2. No column list contains a `PINTEREST_ADS_NOT_QUERYABLE_SYNC` entry.
 *
 * Together these prevent two regressions Pinterest 400s on (HTTP 400 `code:6`
 * "Columns limit exceeded" and the `TOTAL_VIEW_LEAD`/`TOTAL_ENGAGEMENT_LEAD`
 * sync-non-queryable error). The runtime check is paired with unit tests so
 * a future maintainer can't slip a violation through.
 */

import type {
  PinterestAdsAnalyticsOptions,
  PinterestAdsGranularity,
  PinterestAdsReportType,
  PinterestAttributionWindows,
} from "./analytics";
import {
  PINTEREST_ADS_API_BASE,
  PINTEREST_ADS_MAX_COLUMNS_PER_REQUEST,
  PINTEREST_ADS_NOT_QUERYABLE_SYNC,
} from "./analytics";
import type {
  PinterestAdsAsyncReportCreateBody,
  PinterestAdsAsyncReportLevel,
} from "./reports";

// ============================================================================
// Per-report-type column lists
// ============================================================================
//
// Each list is a pragmatic subset of the 185-metric catalogue (capped at 30
// per Pinterest's hard limit). Picks privilege paid (`_1`) clicks/impressions/
// spend + the most-needed conversion + ROAS metrics.

/** Account overview — paid+earned+total deltas + the agency-critical KPIs. */
export const OVERVIEW_COLUMNS: readonly string[] = [
  "IMPRESSION_1",
  "IMPRESSION_2",
  "TOTAL_IMPRESSION",
  "SPEND_IN_DOLLAR",
  "CLICKTHROUGH_1",
  "CLICKTHROUGH_2",
  "TOTAL_CLICKTHROUGH",
  "CTR",
  "CTR_2",
  "OUTBOUND_CLICK_1",
  "OUTBOUND_CLICK_2",
  "OUTBOUND_CTR",
  "ENGAGEMENT_1",
  "ENGAGEMENT_2",
  "TOTAL_ENGAGEMENT",
  "REPIN_1",
  "REPIN_2",
  "ECPC_IN_DOLLAR",
  "ECPM_IN_DOLLAR",
  "TOTAL_CONVERSIONS",
  "TOTAL_CHECKOUT",
  "TOTAL_CHECKOUT_VALUE_IN_MICRO_DOLLAR",
  "CHECKOUT_ROAS",
  "TOTAL_PAGE_VISIT",
  "TOTAL_LEAD",
] as const;

/** Campaigns — overview minus a couple low-priority cols + entity identifiers. */
export const CAMPAIGNS_COLUMNS: readonly string[] = [
  "CAMPAIGN_ID",
  "CAMPAIGN_NAME",
  "CAMPAIGN_STATUS",
  "CAMPAIGN_OBJECTIVE_TYPE",
  "IMPRESSION_1",
  "TOTAL_IMPRESSION",
  "SPEND_IN_DOLLAR",
  "CLICKTHROUGH_1",
  "TOTAL_CLICKTHROUGH",
  "CTR",
  "OUTBOUND_CLICK_1",
  "OUTBOUND_CTR",
  "ENGAGEMENT_1",
  "TOTAL_ENGAGEMENT",
  "REPIN_1",
  "ECPC_IN_DOLLAR",
  "ECPM_IN_DOLLAR",
  "TOTAL_CONVERSIONS",
  "TOTAL_CHECKOUT",
  "TOTAL_CHECKOUT_VALUE_IN_MICRO_DOLLAR",
  "CHECKOUT_ROAS",
  "TOTAL_PAGE_VISIT",
  "TOTAL_LEAD",
] as const;

/** Ad groups — same KPIs as campaigns + ad-group identifiers. */
export const AD_GROUPS_COLUMNS: readonly string[] = [
  "AD_GROUP_ID",
  "AD_GROUP_NAME",
  "AD_GROUP_STATUS",
  "AD_GROUP_ENTITY_STATUS",
  "IMPRESSION_1",
  "TOTAL_IMPRESSION",
  "SPEND_IN_DOLLAR",
  "CLICKTHROUGH_1",
  "TOTAL_CLICKTHROUGH",
  "CTR",
  "OUTBOUND_CLICK_1",
  "OUTBOUND_CTR",
  "ENGAGEMENT_1",
  "TOTAL_ENGAGEMENT",
  "REPIN_1",
  "ECPC_IN_DOLLAR",
  "ECPM_IN_DOLLAR",
  "TOTAL_CONVERSIONS",
  "TOTAL_CHECKOUT",
  "TOTAL_CHECKOUT_VALUE_IN_MICRO_DOLLAR",
  "CHECKOUT_ROAS",
  "TOTAL_PAGE_VISIT",
] as const;

/** Ads — adds creative + pin identifiers + the agency-critical video metrics. */
export const ADS_COLUMNS: readonly string[] = [
  "AD_ID",
  "AD_NAME",
  "PIN_ID",
  "PIN_PROMOTION_TYPE",
  "PIN_CREATIVE_TYPE",
  "IMPRESSION_1",
  "TOTAL_IMPRESSION",
  "SPEND_IN_DOLLAR",
  "CLICKTHROUGH_1",
  "TOTAL_CLICKTHROUGH",
  "CTR",
  "OUTBOUND_CLICK_1",
  "OUTBOUND_CTR",
  "ENGAGEMENT_1",
  "TOTAL_ENGAGEMENT",
  "REPIN_1",
  "ECPC_IN_DOLLAR",
  "ECPM_IN_DOLLAR",
  "TOTAL_CONVERSIONS",
  "TOTAL_CHECKOUT",
  "CHECKOUT_ROAS",
  "VIDEO_MRC_VIEWS_1",
  "VIDEO_3SEC_VIEWS_1",
  "VIDEO_AVG_WATCHTIME_IN_SECOND_1",
  "VIDEO_P95_COMBINED_1",
] as const;

/** Targeting analytics — keyword-level subset (Pinterest exposes only via targeting_analytics). */
export const KEYWORDS_COLUMNS: readonly string[] = [
  "IMPRESSION_1",
  "CLICKTHROUGH_1",
  "CTR",
  "SPEND_IN_DOLLAR",
  "ECPC_IN_DOLLAR",
  "OUTBOUND_CLICK_1",
  "OUTBOUND_CTR",
  "TOTAL_CONVERSIONS",
  "TOTAL_PAGE_VISIT",
  "TOTAL_CHECKOUT",
  "CHECKOUT_ROAS",
] as const;

/** Targeting analytics — age × gender. Pinterest blocks demographics for accounts <100 audience. */
export const AGE_GENDER_COLUMNS: readonly string[] = [
  "IMPRESSION_1",
  "CLICKTHROUGH_1",
  "CTR",
  "SPEND_IN_DOLLAR",
  "ECPC_IN_DOLLAR",
  "TOTAL_CONVERSIONS",
  "TOTAL_CHECKOUT",
] as const;

/** Targeting analytics — geo (country + region + placement multi-dim). */
export const GEO_COLUMNS: readonly string[] = [
  "IMPRESSION_1",
  "CLICKTHROUGH_1",
  "CTR",
  "SPEND_IN_DOLLAR",
  "ECPC_IN_DOLLAR",
  "TOTAL_CONVERSIONS",
  "TOTAL_CHECKOUT",
] as const;

/** Targeting analytics — pinner-interest + targeted-interest (Pinterest's audience-graph signal). */
export const INTERESTS_COLUMNS: readonly string[] = [
  "IMPRESSION_1",
  "CLICKTHROUGH_1",
  "CTR",
  "SPEND_IN_DOLLAR",
  "ECPC_IN_DOLLAR",
  "TOTAL_CONVERSIONS",
  "TOTAL_CHECKOUT",
] as const;

/** Map from internal `PinterestAdsReportType` to its column subset. */
export const COLUMNS_BY_REPORT_TYPE: Record<PinterestAdsReportType, readonly string[]> = {
  overview: OVERVIEW_COLUMNS,
  campaigns: CAMPAIGNS_COLUMNS,
  adGroups: AD_GROUPS_COLUMNS,
  ads: ADS_COLUMNS,
  keywords: KEYWORDS_COLUMNS,
  ageGender: AGE_GENDER_COLUMNS,
  geo: GEO_COLUMNS,
  interests: INTERESTS_COLUMNS,
};

/** Per-report-type targeting_types value list — only meaningful for keywords/ageGender/geo/interests. */
export const TARGETING_TYPES_BY_REPORT_TYPE: Partial<Record<PinterestAdsReportType, readonly string[]>> = {
  keywords: ["KEYWORD"],
  ageGender: ["AGE_BUCKET_AND_GENDER"],
  geo: ["COUNTRY", "REGION", "PLACEMENT"],
  interests: ["PINNER_INTEREST", "TARGETED_INTEREST"],
};

/**
 * Per-report-type granularity. Time-series report types use DAY; breakdown
 * report types use TOTAL (one row per dim slice).
 */
export const GRANULARITY_BY_REPORT_TYPE: Record<PinterestAdsReportType, PinterestAdsGranularity> = {
  overview: "DAY",
  campaigns: "DAY",
  adGroups: "DAY",
  ads: "DAY",
  keywords: "TOTAL",
  ageGender: "TOTAL",
  geo: "TOTAL",
  interests: "TOTAL",
};

// ============================================================================
// Module-load invariants — run once at import time
// ============================================================================

(function assertColumnListInvariants(): void {
  const notQueryable = new Set<string>(PINTEREST_ADS_NOT_QUERYABLE_SYNC);
  for (const [reportType, columns] of Object.entries(COLUMNS_BY_REPORT_TYPE)) {
    if (columns.length === 0) {
      throw new Error(
        `Pinterest Ads report-builders: empty column list for "${reportType}"`,
      );
    }
    if (columns.length > PINTEREST_ADS_MAX_COLUMNS_PER_REQUEST) {
      throw new Error(
        `Pinterest Ads report-builders: "${reportType}" has ${columns.length} columns, exceeds ${PINTEREST_ADS_MAX_COLUMNS_PER_REQUEST}-cap`,
      );
    }
    for (const col of columns) {
      if (notQueryable.has(col)) {
        throw new Error(
          `Pinterest Ads report-builders: "${reportType}" column "${col}" is not queryable via sync analytics`,
        );
      }
    }
    // Also catch dupes — Pinterest doesn't 400 on these but it wastes budget.
    const seen = new Set<string>();
    for (const col of columns) {
      if (seen.has(col)) {
        throw new Error(
          `Pinterest Ads report-builders: "${reportType}" has duplicate column "${col}"`,
        );
      }
      seen.add(col);
    }
  }
})();

// ============================================================================
// URL / body composers
// ============================================================================

export interface BuildSyncAnalyticsRequestInput {
  readonly accountId: string;
  readonly dateRange: { readonly start: Date; readonly end: Date };
  readonly reportType: PinterestAdsReportType;
  /** Top-N entity IDs for entity-scoped reports (campaigns/adGroups/ads). */
  readonly entityIds?: ReadonlyArray<string>;
  /** Optional override; defaults to `COLUMNS_BY_REPORT_TYPE[reportType]`. */
  readonly columns?: ReadonlyArray<string>;
  /** Optional override; defaults to `GRANULARITY_BY_REPORT_TYPE[reportType]`. */
  readonly granularity?: PinterestAdsGranularity;
  readonly attribution?: PinterestAttributionWindows;
  readonly conversionReportTime?: "TIME_OF_AD_ACTION" | "TIME_OF_CONVERSION";
}

/**
 * Composes the per-call options object every sync analytics fetcher consumes.
 *
 * Returns the raw `accountId`, target sub-path and `targetingTypes` so the
 * adapter's pipeline can dispatch to the right `analytics.ts` function.
 */
export interface BuildSyncAnalyticsRequestOutput {
  readonly accountId: string;
  readonly options: PinterestAdsAnalyticsOptions;
  /** Entity IDs the caller MUST pass to campaigns/adGroups/ads analytics. */
  readonly entityIds?: ReadonlyArray<string>;
  /** Targeting types the caller MUST pass to targeting_analytics. */
  readonly targetingTypes?: ReadonlyArray<string>;
}

export function buildSyncAnalyticsRequest(
  input: BuildSyncAnalyticsRequestInput,
): BuildSyncAnalyticsRequestOutput {
  const columns = input.columns ?? COLUMNS_BY_REPORT_TYPE[input.reportType];
  const granularity = input.granularity ?? GRANULARITY_BY_REPORT_TYPE[input.reportType];
  const targetingTypes = TARGETING_TYPES_BY_REPORT_TYPE[input.reportType];

  const options: PinterestAdsAnalyticsOptions = {
    startDate: dateToYearMonthDay(input.dateRange.start),
    endDate: dateToYearMonthDay(input.dateRange.end),
    columns,
    granularity,
    attribution: input.attribution,
    conversionReportTime: input.conversionReportTime,
  };
  return {
    accountId: input.accountId,
    options,
    entityIds: input.entityIds,
    targetingTypes,
  };
}

export interface BuildAsyncReportBodyInput {
  readonly level: PinterestAdsAsyncReportLevel;
  readonly dateRange: { readonly start: Date; readonly end: Date };
  readonly columns: ReadonlyArray<string>;
  readonly granularity?: PinterestAdsGranularity;
  readonly attribution?: PinterestAttributionWindows;
  readonly conversionReportTime?: "TIME_OF_AD_ACTION" | "TIME_OF_CONVERSION";
  readonly campaignIds?: ReadonlyArray<string>;
  readonly adGroupIds?: ReadonlyArray<string>;
  readonly adIds?: ReadonlyArray<string>;
}

/**
 * Composes the JSON body for `POST /v5/ad_accounts/{id}/reports`.
 *
 * Hard-codes `report_format: "JSON"` and `attribution_types: ["INDIVIDUAL"]`
 * (Pinterest defaults to INDIVIDUAL; HOUSEHOLD changes attribution semantics
 * and is out of scope for Story 19.5).
 */
export function buildAsyncReportBody(
  input: BuildAsyncReportBodyInput,
): PinterestAdsAsyncReportCreateBody {
  return {
    start_date: dateToYearMonthDay(input.dateRange.start),
    end_date: dateToYearMonthDay(input.dateRange.end),
    level: input.level,
    granularity: input.granularity ?? "DAY",
    columns: input.columns,
    click_window_days: input.attribution?.clickWindowDays,
    engagement_window_days: input.attribution?.engagementWindowDays,
    view_window_days: input.attribution?.viewWindowDays,
    conversion_report_time: input.conversionReportTime,
    attribution_types: ["INDIVIDUAL"],
    report_format: "JSON",
    campaign_ids: input.campaignIds,
    ad_group_ids: input.adGroupIds,
    ad_ids: input.adIds,
  };
}

// ============================================================================
// Date helper
// ============================================================================

/**
 * UTC YYYY-MM-DD encoder. Pinterest interprets dates against the ad-account
 * time zone server-side, so the *encoding* doesn't shift day boundaries —
 * but using UTC accessors avoids timezone drift across Worker isolates.
 */
export function dateToYearMonthDay(d: Date): string {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) {
    throw new Error("dateToYearMonthDay: invalid Date");
  }
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

// ============================================================================
// API base re-export — keeps callers from importing from `./api` directly
// ============================================================================

export { PINTEREST_ADS_API_BASE };
